import React, { createContext, useContext, useState, ReactNode } from "react";

interface FocusModeContextType {
  // Focus mode state per conversation
  focusedConversations: Set<string>;

  // Actions
  enterFocusMode: (conversationId: string) => void;
  exitFocusMode: (conversationId: string) => void;
  toggleFocusMode: (conversationId: string) => void;
  isConversationInFocusMode: (conversationId: string) => boolean;

  // Global focus mode state (if any conversation is in focus mode)
  hasAnyFocusedConversation: boolean;
}

const FocusModeContext = createContext<FocusModeContextType | null>(null);

export function useFocusMode() {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error("useFocusMode must be used within a FocusModeProvider");
  }
  return context;
}

interface FocusModeProviderProps {
  children: ReactNode;
}

export function FocusModeProvider({ children }: FocusModeProviderProps) {
  const [focusedConversations, setFocusedConversations] = useState<Set<string>>(new Set());

  const enterFocusMode = (conversationId: string) => {
    setFocusedConversations((prev) => new Set(prev).add(conversationId));
  };

  const exitFocusMode = (conversationId: string) => {
    setFocusedConversations((prev) => {
      const newSet = new Set(prev);
      newSet.delete(conversationId);
      return newSet;
    });
  };

  const toggleFocusMode = (conversationId: string) => {
    if (focusedConversations.has(conversationId)) {
      exitFocusMode(conversationId);
    } else {
      enterFocusMode(conversationId);
    }
  };

  const isConversationInFocusMode = (conversationId: string) => {
    return focusedConversations.has(conversationId);
  };

  const hasAnyFocusedConversation = focusedConversations.size > 0;

  const contextValue: FocusModeContextType = {
    focusedConversations,
    enterFocusMode,
    exitFocusMode,
    toggleFocusMode,
    isConversationInFocusMode,
    hasAnyFocusedConversation,
  };

  return <FocusModeContext.Provider value={contextValue}>{children}</FocusModeContext.Provider>;
}
